// Frontend integration for promo code redemption.
//
// `codeRedemption` is an onRequest (HTTP) function that returns a structured
// body `{ status, code, message, details: { status } }`. We call it with fetch
// (not httpsCallable) so we can read `details.status` and show the correct
// message — e.g. "already redeemed" vs "invalid code" — exactly like the mobile
// app does. (httpsCallable discards that body and collapses every 400 to
// "invalid-argument", which is why an already-redeemed code showed "invalid
// code format".)
import { app, auth } from './firebase';
import { t } from '../i18n';

const FUNCTION_REGION = 'europe-west2';

const codeRedemptionUrl = (): string => {
  const projectId = app.options.projectId;
  return `https://${FUNCTION_REGION}-${projectId}.cloudfunctions.net/codeRedemption`;
};

/** Map the backend `details.status` (or fallback HTTP status) to a user message. */
const messageForStatus = (status?: string, httpStatus?: number): string => {
  switch (status) {
    case 'active-code-exists':
      return t('errors.promoCode.activeCode');
    case 'already-exists':
    case 'promo-code-already-redeemed':
    case 'deal-code-already-redeemed':
    case 'deal-code-already-redeemed-by-user':
    case 'user-already-redeemed-deal-code':
    case 'ambassador-code-already-redeemed':
      return t('errors.promoCode.alreadyExists');
    case 'promo-code-expired':
    case 'deal-expired':
    case 'failed-precondition':
      return t('errors.promoCode.expired');
    case 'unauthenticated':
      return t('errors.promoCode.unauthenticated');
    case 'permission-denied':
      return t('errors.promoCode.permissionDenied');
    case 'invalid-argument':
    case 'not-found':
    case 'promo-code-not-found':
    case 'deal-not-found':
    case 'user-not-found':
      return t('errors.promoCode.invalidCode');
    default:
      if (httpStatus === 401) return t('errors.promoCode.unauthenticated');
      if (httpStatus === 404) return t('errors.promoCode.invalidCode');
      if (httpStatus === 429) return t('errors.promoCode.resourceExhausted');
      if (httpStatus && httpStatus >= 500) return t('errors.promoCode.internalError');
      return t('errors.promoCode.default');
  }
};

/**
 * Redeem a promo code for a user.
 */
export async function redeemPromoCode(
  code: string,
  revCatUserId: string
): Promise<{
  success: boolean;
  message?: string;
  error?: string;
}> {
  const upper = code.toUpperCase();
  try {
    const idToken = await auth.currentUser?.getIdToken();
    if (!idToken) {
      return { success: false, error: t('errors.promoCode.unauthenticated') };
    }

    const response = await fetch(codeRedemptionUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({ data: { redeemCode: upper, revCatUserId } }),
    });

    const body = await response.json().catch(() => ({} as any));
    console.log('Promo code redemption response:', response.status, body);

    if (response.ok && (body?.data?.redeemCode === upper || body?.status === 'success')) {
      return { success: true, message: t('promoCode.redeemSuccess') };
    }

    return {
      success: false,
      error: messageForStatus(body?.details?.status, response.status),
    };
  } catch (error: any) {
    console.error('Error redeeming promo code:', error);
    return { success: false, error: t('errors.promoCode.networkError') };
  }
}
